import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Subcategory {
  id: string;
  name: string;
  slug: string;
}

export interface CategoryWithSubcategories {
  id: string;
  name: string;
  slug: string;
  image_url: string | null;
  description: string | null;
  is_featured?: boolean;
  subcategories: Subcategory[];
}

async function fetchCategories(): Promise<CategoryWithSubcategories[]> {
  // Fetch parent categories (those without parent_id)
  const { data: parentCategories, error: parentError } = await supabase
    .from("categories")
    .select("id, name, slug, image_url, description, is_featured")
    .is("parent_id", null)
    .order("name");

  if (parentError) {
    console.error("Error fetching categories:", parentError);
    throw parentError;
  }

  if (!parentCategories || parentCategories.length === 0) {
    return [];
  }

  // Fetch all subcategories (those with parent_id)
  const { data: subcategories, error: subError } = await supabase
    .from("categories")
    .select("id, name, slug, parent_id")
    .not("parent_id", "is", null);

  if (subError) {
    console.error("Error fetching subcategories:", subError);
    throw subError;
  }

  // Map subcategories to their parent categories
  const categoriesWithSubs: CategoryWithSubcategories[] = parentCategories.map(
    (category) => ({
      ...category,
      subcategories: (subcategories || [])
        .filter((sub) => sub.parent_id === category.id)
        .map((sub) => ({
          id: sub.id,
          name: sub.name,
          slug: sub.slug,
        })),
    })
  );

  return categoriesWithSubs;
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });
}

// Fallback static categories for when DB is empty or loading
export const fallbackCategories: CategoryWithSubcategories[] = [
  {
    id: "1",
    name: "Sarees",
    slug: "sarees",
    image_url: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&h=800&fit=crop",
    description: "Timeless elegance woven in every thread",
    subcategories: [
      { id: "1-1", name: "Banarasi Silk", slug: "banarasi-silk" },
      { id: "1-2", name: "Kanjivaram", slug: "kanjivaram" },
      { id: "1-3", name: "Chanderi", slug: "chanderi" },
      { id: "1-4", name: "Cotton Sarees", slug: "cotton-sarees" },
    ],
  },
  {
    id: "2",
    name: "Lehengas",
    slug: "lehengas",
    image_url: "https://images.unsplash.com/photo-1583391733956-6c78276eedc5?w=600&h=800&fit=crop",
    description: "Bridal dreams crafted with perfection",
    subcategories: [
      { id: "2-1", name: "Bridal Lehengas", slug: "bridal-lehengas" },
      { id: "2-2", name: "Party Wear", slug: "party-wear" },
    ],
  },
  {
    id: "3",
    name: "Fabrics",
    slug: "fabrics",
    image_url: "https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&h=800&fit=crop",
    description: "Premium fabrics for your creative vision",
    subcategories: [
      { id: "3-1", name: "Pure Silk", slug: "pure-silk" },
      { id: "3-2", name: "Brocade", slug: "brocade" },
    ],
  },
  {
    id: "4",
    name: "Suits & Sets",
    slug: "suits-sets",
    image_url: "https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=600&h=800&fit=crop",
    description: "Graceful ensembles for every occasion",
    subcategories: [
      { id: "4-1", name: "Anarkali Suits", slug: "anarkali-suits" },
      { id: "4-2", name: "Sharara Sets", slug: "sharara-sets" },
    ],
  },
];
